// Lib
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';

// Utils
import { prop } from '../../../../../common/utils/immutableHelper';

// Selector
import { selectVisibleConnectionStatus } from '../../../../utils/socket/socketConnectionSelector';

// Components
import Spinner from '../../../../components/loaders/Spinner';

// Types
import { TIMES } from '../../../../../common/utils/timeUtil';
import { VisibleConnectionStatus } from '../../../../offline/visibleConnectionStatus/visibleConnectionStatusTypes';

const SAVING_MESSAGE_DISPLAY_DURATION = 2 * TIMES.SECOND;
const SAVED_MESSAGE_DISPLAY_DURATION = 2 * TIMES.SECOND;

/**
 * Shows the "Saving..." or "Trying to reconnect..." feedback in the header.
 */
const SavingFeedbackUtil: React.FC = () => {
    const lastAction = useSelector(prop('lastSaved'));
    const visibleConnectionStatus = useSelector(selectVisibleConnectionStatus);
    const isVisibleConnectionStatusConnected = visibleConnectionStatus === VisibleConnectionStatus.connected;
    const isVisibleConnectionStatusInterrupted = visibleConnectionStatus === VisibleConnectionStatus.interrupted;

    const [showLoading, setShowLoading] = useState(false);
    const [hidden, setHidden] = useState(true);

    const hide = useCallback(
        debounce(() => {
            setHidden(true);
        }, SAVING_MESSAGE_DISPLAY_DURATION + SAVED_MESSAGE_DISPLAY_DURATION),
        [],
    );

    const stopSaving = useCallback(
        debounce(() => {
            setShowLoading(false);
            hide();
        }, SAVING_MESSAGE_DISPLAY_DURATION),
        [],
    );

    // Clear the debounced functions on unmount
    useEffect(
        () => () => {
            hide.cancel();
            stopSaving.cancel();
        },
        [],
    );

    useEffect(() => {
        if (!isVisibleConnectionStatusConnected) return;

        setShowLoading(true);
        setHidden(false);

        stopSaving();
        hide.cancel();
    }, [lastAction]);

    if (isVisibleConnectionStatusInterrupted) {
        return (
            <div className="SavingFeedbackUtil CurrentBoardHeaderStatus">
                <Spinner show />
                <span className="connection-interrupted">Trying to reconnect...</span>
            </div>
        );
    }

    return (
        <div className={classNames('SavingFeedbackUtil CurrentBoardHeaderStatus', { hidden })}>
            {showLoading ? <span className="saving">Saving...</span> : <span className="save">Saved</span>}
        </div>
    );
};

export default SavingFeedbackUtil;
